/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/


import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { HeaderEnum, PersonInterface, ContactInterface } from "@malmur-technologies/medicalsystem-shared";
import Typography from "components/generals/Typography";
import Box from "components/generals/Box";
import useHttp from "hooks/http.hook";
import { useEffect, useState } from "react";
import { CoreAPI_URLs } from "constants/http.const";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';


/**
 * Properties for the HeaderPersonInformation component.
 * 
 * @interface HeaderProps
 * @property {Partial<PersonInterface>} [personInformation] - Person data to display
 * @property {boolean} [progressNote] - Whether displayed in progress note context (hides photo)
 */
interface HeaderProps {
  personInformation?: Partial<PersonInterface>;
  progressNote?: boolean;
}

/**
 * Person information header card component.
 * Displays person demographics with profile photo.
 * 
 * Features:
 * - Profile photo with authenticated blob loading
 * - Loading indicator with progress for photo
 * - Fallback to default user icon
 * - Person name and contact information
 * - Date of birth and calculated age
 * - Formatted phone numbers
 * - Address details (street, city, state, ZIP)
 * - Responsive grid layout
 * - Conditional photo display (hidden in progress notes)
 * - Automatic person data refresh via API
 * 
 * Photo Loading:
 * - Uses useAuthenticatedAsset hook for secure blob access
 * - Shows CircularProgress during load
 * - Displays load progress percentage
 * - Falls back to default icon on error
 * 
 * Data Display:
 * - Name: Full name from person object
 * - Age: Calculated from date of birth
 * - DOB: Formatted as YYYY-MM-DD
 * - Phone: Primary phone number
 * - Address: Complete address with city, state, ZIP
 * 
 * Layout Modes:
 * - Standard: Shows photo + demographics
 * - Progress Note: Demographics only (progressNote=true)
 * 
 * @param {HeaderProps} props - Component properties
 * @param {Partial<PersonInterface>} props.personInformation - Person data
 * @param {boolean} props.progressNote - Hide photo if true
 * 
 * @returns {JSX.Element} Person information header card
 */
function HeaderEmergencyContactInformation({
  personInformation
}: Readonly<HeaderProps>): JSX.Element {
  const [,, sendRequest] = useHttp();
  const [emergencyContact, setEmergencyContact] = useState<Partial<ContactInterface>[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (personInformation) {
      sendRequest(CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS, onGetPerson, "GET", undefined, [
        {
          header: HeaderEnum.RESOURCE_ID,
          value: personInformation.id,
        },
      ]);
    }
  }, [personInformation]);

  const onGetPerson = (person: PersonInterface) => {
    if (person) {
      setEmergencyContact(person.emergencyContacts ? person.emergencyContacts : []);
      setCurrentIndex(0);
    }
  };

  const nextContact = () => {
    if (currentIndex < emergencyContact.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevContact = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const isFirstContact = currentIndex === 0;
  const isLastContact = currentIndex === emergencyContact.length - 1;

  return (
    <Card id="profile" className="card-base" sx={{minWidth: 260, height: '100%', display: 'flex', flexDirection: 'column', padding:0 }}>
      <Box display="flex" flexDirection="column" height="100%" p={2}>
        
        {emergencyContact.length > 0 ? (
          <>
       <Typography variant="h5" align="center" className="title-secondary" fontWeight="medium" >
          {t("headerPersonInformation.emergencyContact")}
        </Typography>
            <Box flex={1} display="flex" flexDirection="column" justifyContent="center" textAlign="center" overflow="hidden">
              <Typography variant="h6" className="title-secondary" fontWeight="medium">
                {emergencyContact[currentIndex].name} {emergencyContact[currentIndex].lastname}
              </Typography>
              <Typography variant="body2" color="text" fontWeight="bold" component="div" noWrap>
                {emergencyContact[currentIndex].phone}
              </Typography>
              <Typography variant="body2" color="text" fontWeight="bold" component="div" noWrap>
                {t("headerPersonInformation.relation")}: {emergencyContact[currentIndex].relation}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mt={1}>
              <IconButton onClick={prevContact} disabled={isFirstContact} sx={{ flexShrink: 0 }}>
                <ArrowLeftIcon />
              </IconButton>
              <Typography variant="body2" align="center" className="title-secondary" fontWeight="light">
                ({currentIndex + 1}/{emergencyContact.length})
              </Typography>
              <IconButton onClick={nextContact} disabled={isLastContact} sx={{ flexShrink: 0 }}>
                <ArrowRightIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body1" fontWeight="medium" align="center">
              {t("headerPersonInformation.noEmergencyContactAvailable")}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}
export default HeaderEmergencyContactInformation;
